import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';

import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { useState } from 'react';
import componentConstants from '../../../core-utils/messages/componentConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import { DataErrorProps } from '../CreateNewUserModal';
import { useStores } from '../../../stores';
import { toJS } from 'mobx';

export const cellStyles4 = {
  color: patientAccessTheme.palette.text.mediumemphasis,
  fontSize: '14px',
  fontWeight: 400,
};

export interface CreateCareNavModalProps {
  open: boolean;
  handleClose: () => any;
  handleSave: () => any;
  isSaveButtonDisabled: boolean;
  isEditModal?: boolean;
  assignCareNavState: [any, React.Dispatch<React.SetStateAction<any>>];
  groups: any[];
}

const CreateCareNavModal: React.FC<CreateCareNavModalProps> = ({
  open,
  handleClose,
  handleSave,
  isSaveButtonDisabled,
  isEditModal,
  assignCareNavState,
  groups,
}) => {
  const useStyles = makeStyles()({
    modal: {
      width: '720px',
      height: '655px',
      background: patientAccessTheme.palette.status.white,
      borderRadius: '8px',
      padding: '24px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      flexDirection: 'column',
    },
    inputFieldsGrid: {
      width: '656px',
      marginTop: '20px',
      background: '#FBFBFE',
      padding: '24px 24px',
      borderRadius: '8px',
    },
    selectFields: {
      display: 'flex',
      flexDirection: 'row',
      marginBottom: '6px',
    },
    cancelButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border: '1px solid ' + patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    saveButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border:
        '1px solid ' + isSaveButtonDisabled
          ? patientAccessTheme.palette.grey[500]
          : patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
  });
  const { classes } = useStyles();
  const constants =
    componentConstants.ORGANISMS.CARE_NAV_MODALS.CREATE_CARE_NAV_MODAL;

  const userDetailsStore = useStores().userDetailsStore;

  const [assignData, setAssignData] = assignCareNavState;
  const [disableSave, setDisableSave] = useState<boolean>(!isEditModal);
  const [dataInvalid, setDataInvalid] = useState<DataErrorProps>({
    firstName: false,
    lastName: false,
    emailID: false,
  });

  const validateData = (name: string, value?: string) => {
    switch (name) {
      case 'firstName':
      case 'lastName':
        setDataInvalid((prevState) => ({
          ...prevState,
          [name]: !value?.match(
            /^[a-zA-Z]+([!@#$%^&*()_+-=\[\];\',.\/<>?:" {}|0-9a-zA-Z]*[!@#$%^&*()_+-=\[\];\',.\/<>?:"{}|0-9a-zA-Z]+)$/,
          ),
        }));
        break;

      case 'email':
        setDataInvalid((prevState) => ({
          ...prevState,
          emailID: !value?.match(
            '^[a-zA-Z0-9_+&*-]+(?:\\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,7}$',
          ),
        }));
        break;
    }
    if (dataInvalid.firstName || dataInvalid.lastName || dataInvalid.emailID) {
      setDisableSave(true);
    } else {
      setDisableSave(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Grid container className={classes.modal}>
        <Grid>
          <Typography
            fontSize="18px"
            lineHeight="26px"
            fontType="semibold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="4px 0px 6px 0px"
          >
            {isEditModal ? constants.EDIT_TITLE : constants.ADD_TITLE}
          </Typography>
          <Typography
            fontSize="14px"
            lineHeight="24px"
            fontType="normal"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
            margin="4px 0px 6px 0px"
          >
            {isEditModal ? constants.EDIT_DESC : constants.ADD_DESC}
          </Typography>
        </Grid>

        <Grid container className={classes.inputFieldsGrid}>
          <Grid className={classes.selectFields}>
            <Grid style={{ marginRight: '30px' }}>
              <ReportCreateNewLabelField
                label={constants.FIRSTNAME}
                required
                component={
                  <TextField
                    value={assignData.firstName}
                    error={dataInvalid?.firstName}
                    placeholder={'yyyy'}
                    height="36px"
                    width="289px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      validateData('firstName', e.target.value);
                      setAssignData((prev: any) => ({
                        ...prev,
                        firstName: e.target.value,
                      }));
                    }}
                  />
                }
              />
            </Grid>
            <Grid>
              <ReportCreateNewLabelField
                label={constants.LASTNAME}
                required
                component={
                  <TextField
                    value={assignData.lastName}
                    error={dataInvalid?.lastName}
                    placeholder={'zzzz'}
                    height="36px"
                    width="289px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      validateData('lastName', e.target.value);
                      setAssignData((prev: any) => ({
                        ...prev,
                        lastName: e.target.value,
                      }));
                    }}
                  />
                }
              />
            </Grid>
          </Grid>

          <Grid className={classes.selectFields}>
            <Grid style={{ marginRight: '30px' }}>
              <ReportCreateNewLabelField
                label={constants.ROLE}
                required
                component={
                  <Dropdown
                    label={'Select'}
                    dropDownOptions={constants.roleOptions}
                    dropDownValues={constants.roleValues}
                    value={assignData.role}
                    handleChange={(e) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        role: e.target.value,
                      }));
                    }}
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '289px',
                    }}
                    labelTop="10px"
                  />
                }
              />
            </Grid>
            <Grid>
              <ReportCreateNewLabelField
                label={constants.EMAIL}
                required
                component={
                  <TextField
                    value={assignData.email}
                    error={dataInvalid?.emailID}
                    placeholder={'abcd@gmail.com'}
                    height="36px"
                    width="289px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      validateData('email', e.target.value);
                      setAssignData((prev: any) => ({
                        ...prev,
                        email: e.target.value,
                      }));
                    }}
                  />
                }
              />
            </Grid>
          </Grid>

          <Grid className={classes.selectFields}>
            <Grid style={{ marginRight: '30px' }}>
              <ReportCreateNewLabelField
                label={constants.CONTACT}
                required
                component={
                  <TextField
                    value={assignData.contact}
                    error={false}
                    placeholder={'1234567890'}
                    height="36px"
                    width="289px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        contact: e.target.value,
                      }));
                    }}
                  />
                }
              />
            </Grid>
            <Grid>
              <ReportCreateNewLabelField
                label={constants.EXPERIENCE}
                component={
                  <TextField
                    value={assignData.experience}
                    error={false}
                    placeholder={'experience'}
                    height="36px"
                    width="289px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        experience: e.target.value,
                      }));
                    }}
                  />
                }
              />
            </Grid>
          </Grid>

          <Grid className={classes.selectFields}>
            <Grid style={{ marginRight: '30px' }}>
              <ReportCreateNewLabelField
                label={constants.GROUP}
                component={
                  <AutocompleteWithCheckBoxes
                    width="292px"
                    options={groups}
                    defaultValue={assignData.groups}
                    handleChange={(event: any, value: any) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        groups: value,
                      }));
                    }}
                    placeholder={assignData.groups.length === 0 ? 'Select' : ''}
                  />
                }
              />
            </Grid>
            <Grid>
              <ReportCreateNewLabelField
                label={constants.SPECIALITY}
                component={
                  <TextField
                    value={assignData.speciality}
                    error={false}
                    placeholder={'speciality'}
                    height="36px"
                    width="289px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        speciality: e.target.value,
                      }));
                    }}
                  />
                }
              />
            </Grid>
          </Grid>

          <Grid className={classes.selectFields}>
            <Grid style={{ marginRight: '30px' }}>
              <ReportCreateNewLabelField
                label={constants.FEATURES}
                component={
                  <AutocompleteWithCheckBoxes
                    width="611px"
                    options={
                      toJS(userDetailsStore.allScreens)?.careNavigation || []
                    }
                    defaultValue={assignData.features}
                    handleChange={(event: any, value: any) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        features: value,
                      }));
                    }}
                    placeholder={constants.SELECTFEATURE}
                  />
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          style={{
            position: 'absolute',
            bottom: 0,
            margin: '0px 0px 24px 390px',
          }}
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid style={{ marginRight: '12px' }}>
            <Button
              label={constants.CANCEL}
              variant="outlined"
              className={classes.cancelButton}
              onClick={handleClose}
            />
          </Grid>
          <Grid>
            <Button
              label={isEditModal ? constants.UPDATE : constants.SUBMIT}
              variant="contained"
              className={classes.saveButton}
              disabled={isSaveButtonDisabled || disableSave}
              onClick={() => {
                handleSave && handleSave();
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
};
export default CreateCareNavModal;
